import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Minus, Plus, Check, Info, Sparkles, Gift, FileText, Phone } from 'lucide-react';
import { 
  AGREEMENT_TERMS, 
  SERVICE_CHARGES,
  SERVICE_INFO,
  COMPANY_INFO,
  calculatePricing, 
  getTotalMatCount 
} from '../data/mock';
import { Checkbox } from '../components/ui/checkbox';
import api from '../services/api';
import AddressAutocomplete from '../components/AddressAutocomplete';

// Full agreement text
const AGREEMENT_TEXT = `RENTAL SERVICE AGREEMENT – SELF-CHECKOUT TERMS

This Rental Service Agreement ("Agreement") is entered into electronically between Executive Mat Service East Ltd. operating as Executive Mat Service (further referred to as the Company) and the customer completing this self-checkout transaction (further referred to as the Customer).

By proceeding with self-checkout and placing an order, the Customer acknowledges that they have read, understood, and agreed to be bound by the terms and conditions set forth below.

The Customer has ordered from Company certain merchandise (either as purchased items or rented items) ("Merchandise") and related services ("Services") as selected during checkout and/or listed in associated schedules or confirmations, at the prices and upon the conditions outlined therein. Additional Merchandise or Services requested by Customer and agreed to in writing or electronically will also be subject to the terms of this Agreement.

All rented Merchandise supplied to Customer remains the property of Company. The Customer guarantees payment of all costs, including reasonable attorney's fees, incurred by the Company in enforcing any term of this Agreement.

1) TERM

This Agreement becomes effective on the date the Customer completes checkout and confirms acceptance of these terms. The initial term is sixty (60) months, consisting of consecutive billings based on the selected service frequency. The term commences on the date of initial delivery of Merchandise and shall automatically renew for the same period unless terminated by written notice from either the Company or the Customer via registered mail no more than 180 days and no less than 90 days prior to the expiration of the then-current term.

2) PRICES AND PAYMENTS

All charges are based upon the total Merchandise covered by this Agreement and may change based on Customer adjustments to inventory quantities. Customer agrees to pay all service-related charges presented during checkout or subsequently added in writing and agrees to a minimum weekly charge equal to seventy-five percent (75%) of the annual value of the contracted Services.

Prices shall be increased annually by the greater of the Consumer Price Index (CPI) or five percent (5%) without notice. Company reserves the right to adjust pricing due to unforeseen supply changes or competitive market disruptions. Customer may reject such adjustment by notifying Company within thirty (30) days of the adjusted billing. If rejected, Company may cancel this Agreement with thirty (30) days' notice.

Invoices are due NET 15 days from the invoice date. Past due balances accrue interest at two percent (2%) per month (24% annually).

3) MERCHANDISE

If Customer is supplied with any item not part of Company's regular stock ("Specialty Items"), Customer agrees to purchase all such Specialty Items at replacement cost upon discontinuance of service for any reason. Lost, destroyed, or excessively damaged Merchandise will be charged at replacement cost.

If Customer is enrolled in Company's Customer Advantage Program (CAP), applicable replacement charges are waived in accordance with CAP terms. Merchandise not returned upon termination shall be paid for at Company's then-current replacement rates.

4) PERFORMANCE GUARANTEE

Company will provide regularly scheduled deliveries of Merchandise processed in accordance with generally accepted textile rental industry standards and will replace Merchandise worn through normal wear and tear at no charge.

If Customer has a service concern:
a. Customer must provide written notice detailing the issue.
b. Company shall have forty-five (45) days to remedy the issue.
c. Customer agrees to implement reasonable recommendations provided by Company.
d. If unresolved through no fault of Customer, Services may be cancelled with thirty (30) days' written notice, subject to payment of all accrued charges and return of all Merchandise.

5) DELIVERIES

5.1 Company will use reasonable efforts to accommodate delivery preferences but reserves scheduling discretion. Customer agrees facilities will be accessible during regular business hours (Monday–Friday, 9:00 a.m.–5:00 p.m.) unless otherwise agreed in writing.

5.2 Customer shall inspect all Merchandise upon delivery. Absence at delivery constitutes acceptance.

5.3 Damaged Merchandise must be removed from use immediately and reported to Company.

5.4 Merchandise shall not be relocated without Company's prior written consent.

5.5 Customer agrees that delivery signatures are not required for invoicing or payment unless explicitly retained by Customer in writing.

6) OBLIGATIONS AND REMEDIES

If Customer terminates or breaches this Agreement prior to the end of the term for any reason other than Company's Performance Guarantee, Customer shall pay liquidated damages equal to fifty percent (50%) of the average weekly Service Fees multiplied by the number of weeks remaining in the current term, calculated based on the highest-billing month during the term. All Merchandise must be returned, and lost or damaged items paid for in accordance with Section 3.

7) INDEMNIFICATION

Customer shall defend, indemnify, and hold harmless the Company and its affiliates from all third-party claims arising from Customer's breach, misuse of Merchandise, failure to inspect or maintain Merchandise, refusal of Services, or negligence or intentional acts by Customer or its representatives.

Company shall not indemnify Customer for claims arising from Customer's use of the Merchandise.

8) LIMITATION OF LIABILITY

Company provides Services and Merchandise in lieu of all other warranties except as expressly stated herein. Company shall not be liable for punitive, consequential, or indirect damages. Customer's maximum recovery is limited to the amount paid to Company in the twelve (12) months preceding any claim, except in cases of Company's gross negligence or wilful misconduct.

9) GENERAL CLAUSES

a. Notices
All notices shall be provided in writing and delivered personally or by registered mail to:

Executive Mat Service East Ltd. operating as Executive Mat Service
#1 – 16 Zatonski Ave
Brantford, ON N3V 1G1
Phone: 1-877-720-6287

and to the Customer at the address provided during checkout or subsequent written notice.

b. Entire Agreement
This Agreement constitutes the entire agreement between the parties and supersedes all prior agreements.

c. Force Majeure
Performance delays due to events beyond a party's control shall be excused for the duration of such delay.

d. Enurement
This Agreement binds and benefits the parties and their permitted successors and assigns.

e. Authority
Customer represents that they have authority to enter into this Agreement electronically.

f. Changes to Terms
Company may modify these terms with thirty (30) days' notice. Customer may terminate within thirty (30) days of notice if the change is material.

ELECTRONIC ACCEPTANCE

By completing self-checkout, submitting an order, or accepting Services electronically, the Customer confirms acceptance of this Agreement and agrees that such electronic acceptance is legally binding and equivalent to a handwritten signature.`;

// Canadian provinces for dropdown
const PROVINCES = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' }
];

// Funnel stage mapping
const STAGE_MAP = {
  1: 'select_mats',
  2: 'agreement_term',
  3: 'business_info',
  4: 'service_terms',
  5: 'agreement',
  6: 'payment',
  7: 'review'
};

const ShopPage = () => {
  const navigate = useNavigate();
  const { token: resumeToken } = useParams();
  const [searchParams] = useSearchParams();
  
  // Intent selection (shown before wizard)
  const [intent, setIntent] = useState(null); // 'get_quote' or 'start_service'
  const [showIntentChoice, setShowIntentChoice] = useState(!resumeToken);
  
  // Backend state
  const [inquiryToken, setInquiryToken] = useState(resumeToken || null);
  const [products, setProducts] = useState([]);
  const [rewardTiers, setRewardTiers] = useState([]);
  const [settings, setSettings] = useState({ checkout_payment_enabled: false, agreement_pdf_url: '' });
  const [isLoading, setIsLoading] = useState(true);
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [cart, setCart] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(60);
  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    streetAddress: '',
    unit: '',
    city: '',
    province: '',
    postalCode: ''
  });
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [hasScrolledAgreement, setHasScrolledAgreement] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [cardInfo, setCardInfo] = useState({
    cardNumber: '',
    expiry: '',
    cvv: '',
    nameOnCard: ''
  });
  const [followUpAcknowledged, setFollowUpAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [quoteComplete, setQuoteComplete] = useState(false);
  const [quoteToken, setQuoteToken] = useState(null);
  const [error, setError] = useState(null);

  // Derived values
  const totalMats = getTotalMatCount(cart);
  const pricing = calculatePricing(cart, selectedTerm);
  const showCustomQuoteSuggestion = totalMats >= 5;
  
  // Build dynamic steps based on payment setting
  const buildSteps = useCallback(() => {
    const baseSteps = [
      { id: 1, title: 'Select Mats', description: 'Choose sizes & quantities', stage: 'select_mats' },
      { id: 2, title: 'Agreement Term', description: 'Select your term length', stage: 'agreement_term' },
      { id: 3, title: 'Business Info', description: 'Your contact details', stage: 'business_info' },
      { id: 4, title: 'Service Terms', description: 'Review expectations', stage: 'service_terms' },
      { id: 5, title: 'Agreement', description: 'Accept service terms', stage: 'agreement' },
    ];
    
    if (settings.checkout_payment_enabled && intent !== 'get_quote') {
      baseSteps.push({ id: 6, title: 'Payment', description: 'Payment method', stage: 'payment' });
      baseSteps.push({ id: 7, title: 'Review', description: 'Confirm & start', stage: 'review' });
    } else {
      baseSteps.push({ id: 6, title: 'Review', description: 'Confirm & submit', stage: 'review' });
    }
    
    return baseSteps;
  }, [settings.checkout_payment_enabled, intent]);
  
  const STEPS = buildSteps();

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Load products, settings, and reward tiers in parallel
        const [productsData, settingsData, tiersData] = await Promise.all([
          api.getProducts(),
          api.getPublicSettings(),
          api.getRewardTiers()
        ]);
        
        setProducts(productsData);
        setSettings(settingsData);
        setRewardTiers(tiersData);
        
        // Initialize cart from products
        setCart(productsData.map(p => ({ id: p.id, sku: p.sku, quantity: 0, price_e4w: p.price_e4w })));
        
        // If we have a resume token, load the inquiry
        if (resumeToken) {
          try {
            const inquiry = await api.getInquiry(resumeToken);
            restoreFromInquiry(inquiry, productsData);
            setShowIntentChoice(false);
            setIntent(inquiry.intent);
          } catch (err) {
            console.error('Failed to load inquiry:', err);
            setError('Could not find your saved order. Starting fresh.');
            setShowIntentChoice(true);
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [resumeToken]);

  // Restore state from inquiry
  const restoreFromInquiry = (inquiry, productsData) => {
    // Restore cart
    if (inquiry.cart_items && inquiry.cart_items.length > 0) {
      const restoredCart = productsData.map(p => {
        const cartItem = inquiry.cart_items.find(ci => ci.sku === p.sku || ci.product_id === p.id);
        return {
          id: p.id,
          sku: p.sku,
          quantity: cartItem ? cartItem.quantity : 0,
          price_e4w: p.price_e4w
        };
      });
      setCart(restoredCart);
    }
    
    // Restore term
    if (inquiry.term_months) {
      setSelectedTerm(inquiry.term_months);
    }
    
    // Restore business info
    if (inquiry.business_info) {
      const bi = inquiry.business_info;
      setBusinessInfo({
        businessName: bi.business_name || '',
        contactName: bi.contact_name || '',
        email: bi.email || '',
        phone: bi.phone || '',
        streetAddress: bi.address?.street_address || '',
        unit: bi.address?.unit || '',
        city: bi.address?.city || '',
        province: bi.address?.province || '',
        postalCode: bi.address?.postal_code || ''
      });
    }
    
    // Determine current step from stage
    const stageToStep = {
      'start': 1,
      'select_mats': 1,
      'agreement_term': 2,
      'business_info': 3,
      'service_terms': 4,
      'agreement': 5,
      'payment': 6,
      'review': 7
    };
    
    const step = stageToStep[inquiry.current_stage] || 1;
    setCurrentStep(step);
    setInquiryToken(inquiry.token);
  };

  // Create or update inquiry on backend
  const syncToBackend = useCallback(async (stepData = {}) => {
    if (!inquiryToken) return;
    
    try {
      // Build cart items for API
      const cartItems = cart.filter(c => c.quantity > 0).map(c => ({
        product_id: c.id,
        sku: c.sku,
        quantity: c.quantity,
        price_e4w: c.price_e4w
      }));
      
      // Build business info for API
      const businessInfoApi = businessInfo.email ? {
        business_name: businessInfo.businessName,
        contact_name: businessInfo.contactName,
        email: businessInfo.email,
        phone: businessInfo.phone,
        address: {
          street_address: businessInfo.streetAddress,
          unit: businessInfo.unit || null,
          city: businessInfo.city,
          province: businessInfo.province,
          postal_code: businessInfo.postalCode
        }
      } : null;
      
      const updateData = {
        cart_items: cartItems.length > 0 ? cartItems : undefined,
        term_months: selectedTerm,
        current_stage: STAGE_MAP[currentStep] || 'select_mats',
        ...stepData
      };
      
      if (businessInfoApi) {
        updateData.business_info = businessInfoApi;
      }
      
      await api.updateInquiry(inquiryToken, updateData);
    } catch (err) {
      console.error('Failed to sync:', err);
    }
  }, [inquiryToken, cart, selectedTerm, currentStep, businessInfo]);

  // Start the wizard after intent selection
  const handleIntentSelect = async (selectedIntent) => {
    setIntent(selectedIntent);
    setShowIntentChoice(false);
    
    try {
      // Create inquiry on backend
      const inquiry = await api.createInquiry(selectedIntent);
      setInquiryToken(inquiry.token);
      
      // Update URL with token for resume capability
      window.history.replaceState(null, '', `/shop/${inquiry.token}`);
    } catch (err) {
      console.error('Failed to create inquiry:', err);
      setError('Failed to start. Please try again.');
    }
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => 
      item.id === productId 
        ? { ...item, quantity: Math.max(0, item.quantity + delta) }
        : item
    ));
  };

  const getFullAddress = () => {
    const parts = [
      businessInfo.unit ? `${businessInfo.unit}-${businessInfo.streetAddress}` : businessInfo.streetAddress,
      businessInfo.city,
      businessInfo.province,
      businessInfo.postalCode
    ].filter(Boolean);
    return parts.join(', ');
  };

  const canProceed = () => {
    const isPaymentStep = settings.checkout_payment_enabled && intent !== 'get_quote' && currentStep === 6;
    const isReviewStep = (settings.checkout_payment_enabled && intent !== 'get_quote') 
      ? currentStep === 7 
      : currentStep === 6;
    
    switch (currentStep) {
      case 1:
        return totalMats > 0;
      case 2:
        return selectedTerm > 0;
      case 3:
        return businessInfo.businessName && 
               businessInfo.contactName && 
               businessInfo.email && 
               businessInfo.phone && 
               businessInfo.streetAddress &&
               businessInfo.city &&
               businessInfo.province &&
               businessInfo.postalCode;
      case 4:
        return true;
      case 5:
        return agreementAccepted && hasScrolledAgreement;
      case 6:
        if (isPaymentStep) {
          return cardInfo.cardNumber && cardInfo.expiry && cardInfo.cvv && cardInfo.nameOnCard;
        }
        // Review step (no payment)
        if (!settings.checkout_payment_enabled || intent === 'get_quote') {
          return intent === 'get_quote' || followUpAcknowledged;
        }
        return true;
      case 7:
        return true;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (currentStep < STEPS.length) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      // Sync to backend
      await syncToBackend({ current_stage: STAGE_MAP[nextStep] });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Sync agreement acceptance
      if (agreementAccepted && inquiryToken) {
        await api.updateInquiry(inquiryToken, {
          agreement_acceptance: {
            terms_version_id: settings.current_agreement_version || 'v1.0',
            accepted_at: new Date().toISOString(),
            ip_address: '',
            user_agent: navigator.userAgent,
            customer_name: businessInfo.contactName,
            customer_email: businessInfo.email,
            business_name: businessInfo.businessName
          }
        });
      }
      
      if (intent === 'get_quote') {
        // Create quote
        const cartItems = cart.filter(c => c.quantity > 0).map(c => ({
          product_id: c.id,
          sku: c.sku,
          quantity: c.quantity,
          price_e4w: c.price_e4w
        }));
        
        const quoteData = {
          business_info: {
            business_name: businessInfo.businessName,
            contact_name: businessInfo.contactName,
            email: businessInfo.email,
            phone: businessInfo.phone,
            address: {
              street_address: businessInfo.streetAddress,
              unit: businessInfo.unit || null,
              city: businessInfo.city,
              province: businessInfo.province,
              postal_code: businessInfo.postalCode
            }
          },
          cart_items: cartItems,
          term_months: selectedTerm
        };
        
        const quote = await api.createQuote(quoteData);
        setQuoteToken(quote.token);
        setQuoteComplete(true);
      } else {
        // Submit order
        await api.submitInquiry(inquiryToken);
        setOrderComplete(true);
      }
    } catch (err) {
      console.error('Submit failed:', err);
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[var(--brand-green)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="body-regular text-[var(--text-secondary)]">Loading...</p>
        </div>
      </div>
    );
  }

  // Quote complete state
  if (quoteComplete) {
    return (
      <QuoteConfirmation 
        pricing={pricing} 
        businessInfo={businessInfo} 
        selectedTerm={selectedTerm} 
        cart={cart} 
        products={products}
        getFullAddress={getFullAddress}
        quoteToken={quoteToken}
      />
    );
  }

  // Order complete state
  if (orderComplete) {
    return (
      <OrderConfirmation 
        pricing={pricing} 
        businessInfo={businessInfo} 
        selectedTerm={selectedTerm} 
        cart={cart}
        products={products}
        getFullAddress={getFullAddress}
        showPaymentFollowUp={!settings.checkout_payment_enabled}
        rewardTiers={rewardTiers}
      />
    );
  }

  // Intent selection screen
  if (showIntentChoice) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container section-padding">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="hero-medium mb-4">Start Your Mat Service</h1>
            <p className="body-large text-[var(--text-secondary)] mb-10">
              Choose how you'd like to proceed
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <button
                onClick={() => handleIntentSelect('start_service')}
                className="card-clean p-8 text-left hover:border-[var(--brand-green)] transition-colors group"
                data-testid="intent-start-service"
              >
                <div className="w-14 h-14 bg-[var(--brand-green)] text-white rounded-full flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <ArrowRight className="w-7 h-7" />
                </div>
                <h2 className="heading-2 text-lg mb-2">Start Service Now</h2>
                <p className="body-regular text-[var(--text-secondary)]">
                  Complete your order online and we'll schedule your first delivery within 1 business day.
                </p>
              </button>
              
              <button
                onClick={() => handleIntentSelect('get_quote')}
                className="card-clean p-8 text-left hover:border-[var(--brand-green)] transition-colors group"
                data-testid="intent-get-quote"
              >
                <div className="w-14 h-14 bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-full flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7" />
                </div>
                <h2 className="heading-2 text-lg mb-2">Get a Quote</h2>
                <p className="body-regular text-[var(--text-secondary)]">
                  Receive a detailed quote you can review and approve when ready. No commitment required.
                </p>
              </button>
            </div>
            
            <p className="body-small text-[var(--text-light)] mt-8">
              Need help? Call us at{' '}
              <a href="tel:1-877-720-6287" className="text-[var(--brand-green)] font-medium hover:underline">
                1-877-720-6287
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="container section-padding-small">
        {/* Starter Plan Notice */}
        <div className="mb-6 p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-light)] text-center">
          <p className="body-small text-[var(--text-secondary)]">
            <span className="font-semibold text-[var(--text-primary)]">Starter Plan</span> — we'll follow up to confirm the best setup and review additional options if needed.
          </p>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
            {error}
          </div>
        )}
        
        {/* Progress Steps */}
        <div className="mb-14">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex items-center gap-2">
                  <div className={`step-number-outline ${currentStep > step.id ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}>
                    {currentStep > step.id ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <span>{step.id}</span>
                    )}
                  </div>
                  <span className={`hidden sm:block text-sm ${
                    currentStep >= step.id ? 'text-[var(--text-primary)] font-medium' : 'text-[var(--text-light)]'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-8 h-[2px] ${
                    currentStep > step.id ? 'bg-[var(--brand-green)]' : 'bg-[var(--border-light)]'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="card-clean">
              <h2 className="heading-2 mb-2">{STEPS[currentStep - 1]?.title}</h2>
              <p className="body-regular text-[var(--text-secondary)] mb-8">
                {STEPS[currentStep - 1]?.description}
              </p>

              {currentStep === 1 && (
                <StepMatSelection 
                  cart={cart} 
                  products={products}
                  updateQuantity={updateQuantity} 
                  totalMats={totalMats}
                  showCustomQuoteSuggestion={showCustomQuoteSuggestion}
                  navigate={navigate}
                />
              )}

              {currentStep === 2 && (
                <StepAgreementTerm 
                  selectedTerm={selectedTerm} 
                  setSelectedTerm={setSelectedTerm}
                  pricing={pricing}
                  cart={cart}
                />
              )}

              {currentStep === 3 && (
                <StepBusinessInfo 
                  businessInfo={businessInfo} 
                  setBusinessInfo={setBusinessInfo} 
                />
              )}

              {currentStep === 4 && <StepServiceTerms />}

              {currentStep === 5 && (
                <StepAgreement 
                  agreementAccepted={agreementAccepted} 
                  setAgreementAccepted={setAgreementAccepted}
                  hasScrolledAgreement={hasScrolledAgreement}
                  setHasScrolledAgreement={setHasScrolledAgreement}
                  agreementPdfUrl={settings.agreement_pdf_url}
                />
              )}

              {currentStep === 6 && settings.checkout_payment_enabled && intent !== 'get_quote' && (
                <StepPayment 
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  cardInfo={cardInfo}
                  setCardInfo={setCardInfo}
                />
              )}

              {((currentStep === 6 && (!settings.checkout_payment_enabled || intent === 'get_quote')) ||
                (currentStep === 7 && settings.checkout_payment_enabled && intent !== 'get_quote')) && (
                <StepReview 
                  cart={cart}
                  products={products}
                  pricing={pricing}
                  selectedTerm={selectedTerm}
                  businessInfo={businessInfo}
                  getFullAddress={getFullAddress}
                  isQuote={intent === 'get_quote'}
                  showPaymentFollowUp={!settings.checkout_payment_enabled && intent !== 'get_quote'}
                  followUpAcknowledged={followUpAcknowledged}
                  setFollowUpAcknowledged={setFollowUpAcknowledged}
                />
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-10 pt-8 border-t border-[var(--border-light)]">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className={`btn-secondary ${currentStep === 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                  data-testid="shop-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                {currentStep < STEPS.length ? (
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className="btn-primary"
                    data-testid="shop-continue-btn"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={!canProceed() || isSubmitting}
                    className="btn-primary"
                    data-testid="shop-submit-btn"
                  >
                    {isSubmitting ? 'Processing...' : (intent === 'get_quote' ? 'Request Quote' : 'Submit My Order')}
                    {!isSubmitting && <ArrowRight className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <OrderSummary 
              cart={cart} 
              products={products}
              pricing={pricing} 
              selectedTerm={selectedTerm}
              updateQuantity={updateQuantity}
              rewardTiers={rewardTiers}
              isQuote={intent === 'get_quote'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Components
const StepMatSelection = ({ cart, products, updateQuantity, totalMats, showCustomQuoteSuggestion, navigate }) => (
  <div>
    <div className="space-y-4">
      {products.map((product) => {
        const cartItem = cart.find(c => c.id === product.id || c.sku === product.sku);
        return (
          <div key={product.id} className="flex items-center justify-between p-5 bg-[var(--bg-secondary)] rounded-lg">
            <div className="flex items-center gap-5">
              <div className="w-20 h-16 bg-white rounded border border-[var(--border-light)] overflow-hidden flex-shrink-0">
                {product.image_url ? (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-[var(--text-secondary)]">{product.dimensions}</span>
                  </div>
                )}
              </div>
              <div>
                <h3 className="heading-3 text-base mb-1">{product.name}</h3>
                <p className="body-small">{product.description}</p>
                <p className="body-regular mt-1">
                  <span className="font-semibold">${product.price_e4w.toFixed(2)}</span>
                  <span className="text-[var(--text-secondary)]"> /4 weeks</span>
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <button 
                onClick={() => updateQuantity(product.id, -1)}
                className="quantity-btn rounded-l-md rounded-r-none"
                disabled={cartItem?.quantity === 0}
                data-testid={`qty-minus-${product.sku}`}
              >
                <Minus className="w-4 h-4" />
              </button>
              <input 
                type="text" 
                value={cartItem?.quantity || 0}
                readOnly
                className="quantity-input"
                data-testid={`qty-input-${product.sku}`}
              />
              <button 
                onClick={() => updateQuantity(product.id, 1)}
                className="quantity-btn rounded-r-md rounded-l-none"
                data-testid={`qty-plus-${product.sku}`}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>

    <div className="notice-info mt-6 flex items-start gap-3">
      <Info className="w-5 h-5 text-[var(--brand-green)] flex-shrink-0 mt-0.5" />
      <p className="body-small">Most locations start with 1–2 mats per entrance.</p>
    </div>

    {showCustomQuoteSuggestion && (
      <div className="mt-4 p-5 bg-[#f0fdf4] border border-[var(--brand-green)] rounded-lg flex items-start gap-4">
        <Sparkles className="w-5 h-5 text-[var(--brand-green)] flex-shrink-0 mt-0.5" />
        <div>
          <p className="body-regular font-semibold text-[var(--text-primary)]">Better Pricing Available</p>
          <p className="body-small text-[var(--text-secondary)] mt-1">
            For orders of 5+ mats, our team can create a custom program with better pricing tailored to your needs.
          </p>
          <Link 
            to="/custom-quote"
            className="text-link mt-3 inline-flex"
          >
            Request a Custom Quote
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    )}

    {totalMats === 0 && (
      <div className="mt-4 p-4 bg-[var(--bg-secondary)] rounded-lg text-center">
        <p className="body-small">Select at least one mat to continue.</p>
      </div>
    )}
  </div>
);

const StepAgreementTerm = ({ selectedTerm, setSelectedTerm, pricing, cart }) => {
  // Calculate pricing at 60 months (best rate) for comparison
  const pricing60 = calculatePricing(cart, 60);
  const weeklyAt60 = pricing60.weeklyEquivalent;

  // Reorder terms: 60 months first (recommended), then 36, then 12
  const orderedTerms = [...AGREEMENT_TERMS].sort((a, b) => b.months - a.months);

  return (
    <div>
      <div className="space-y-3">
        {orderedTerms.map((term) => {
          const termPricing = calculatePricing(cart, term.months);
          const weeklyDiff = termPricing.weeklyEquivalent - weeklyAt60;
          const isRecommended = term.months === 60;
          
          return (
            <button
              key={term.months}
              onClick={() => setSelectedTerm(term.months)}
              className={`selectable w-full p-5 text-left rounded-lg border ${
                selectedTerm === term.months ? 'selected' : 'border-[var(--border-light)]'
              } ${isRecommended ? 'ring-2 ring-[var(--brand-green)] ring-offset-2' : ''}`}
              data-testid={`term-${term.months}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="heading-3 text-base">{term.label}</h3>
                    {isRecommended && (
                      <span className="text-xs font-medium text-[var(--brand-green)] bg-[#ecfdf5] px-2 py-0.5 rounded">
                        Best Value
                      </span>
                    )}
                  </div>
                  {term.discount > 0 ? (
                    <p className="body-small text-[var(--brand-green)]">{Math.round(term.discount * 100)}% discount applied</p>
                  ) : (
                    <p className="body-small text-[#dc2626]">
                      +${weeklyDiff.toFixed(2)}/week vs 60 months
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedTerm === term.months 
                      ? 'border-[var(--brand-green)] bg-[var(--brand-green)]' 
                      : 'border-[var(--border-medium)]'
                  }`}>
                    {selectedTerm === term.months && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selectedTerm !== 60 && (
        <div className="notice-warning mt-6">
          <p className="body-regular text-[#92400e]">
            Selecting a shorter term costs <span className="font-semibold">${(pricing.weeklyEquivalent - weeklyAt60).toFixed(2)} more per week</span> compared to the 60-month term.
          </p>
        </div>
      )}

      {selectedTerm === 60 && (
        <div className="notice-success mt-6">
          <p className="body-regular text-[#065f46]">
            You're getting the <span className="font-semibold">best rate</span> with the 60-month term.
          </p>
        </div>
      )}
    </div>
  );
};

const StepBusinessInfo = ({ businessInfo, setBusinessInfo }) => {
  const handleAddressSelect = React.useCallback((addressData) => {
    console.log('handleAddressSelect called with:', addressData);
    setBusinessInfo(prev => ({
      ...prev,
      streetAddress: addressData.streetAddress,
      city: addressData.city,
      province: addressData.province,
      postalCode: addressData.postalCode
    }));
  }, [setBusinessInfo]);

  const handleStreetChange = React.useCallback((value) => {
    setBusinessInfo(prev => ({ ...prev, streetAddress: value }));
  }, [setBusinessInfo]);

  return (
    <div className="space-y-5">
      <div>
        <label className="block body-regular font-medium mb-2">Business Name *</label>
        <input
          type="text"
          value={businessInfo.businessName}
          onChange={(e) => setBusinessInfo({ ...businessInfo, businessName: e.target.value })}
          className="input-clean"
          placeholder="Your business name"
          data-testid="input-business-name"
        />
      </div>
      <div>
        <label className="block body-regular font-medium mb-2">Contact Name *</label>
        <input
          type="text"
          value={businessInfo.contactName}
          onChange={(e) => setBusinessInfo({ ...businessInfo, contactName: e.target.value })}
          className="input-clean"
          placeholder="Primary contact person"
          data-testid="input-contact-name"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block body-regular font-medium mb-2">Email Address *</label>
          <input
            type="email"
            value={businessInfo.email}
            onChange={(e) => setBusinessInfo({ ...businessInfo, email: e.target.value })}
            className="input-clean"
            placeholder="email@business.com"
            data-testid="input-email"
          />
        </div>
        <div>
          <label className="block body-regular font-medium mb-2">Phone Number *</label>
          <input
            type="tel"
            value={businessInfo.phone}
            onChange={(e) => setBusinessInfo({ ...businessInfo, phone: e.target.value })}
            className="input-clean"
            placeholder="(555) 123-4567"
            data-testid="input-phone"
          />
        </div>
      </div>

      <div className="border-t border-[var(--border-light)] pt-5 mt-6">
        <h3 className="heading-3 text-base mb-4">Service Address</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block body-regular font-medium mb-2">Street Address *</label>
            <AddressAutocomplete
              value={businessInfo.streetAddress}
              onChange={handleStreetChange}
              onAddressSelect={handleAddressSelect}
              placeholder="Start typing your address..."
            />
            <p className="text-xs text-[var(--text-light)] mt-1">Start typing to see address suggestions</p>
          </div>
          
          <div>
            <label className="block body-regular font-medium mb-2">Unit / Suite (Optional)</label>
            <input
              type="text"
              value={businessInfo.unit}
              onChange={(e) => setBusinessInfo({ ...businessInfo, unit: e.target.value })}
              className="input-clean"
              placeholder="Suite 100"
              data-testid="input-unit"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block body-regular font-medium mb-2">City *</label>
              <input
                type="text"
                value={businessInfo.city}
                onChange={(e) => setBusinessInfo({ ...businessInfo, city: e.target.value })}
                className="input-clean"
                placeholder="Toronto"
                data-testid="input-city"
              />
            </div>
            <div>
              <label className="block body-regular font-medium mb-2">Province *</label>
              <select
                value={businessInfo.province}
                onChange={(e) => setBusinessInfo({ ...businessInfo, province: e.target.value })}
                className="input-clean"
                data-testid="input-province"
              >
                <option value="">Select province</option>
                {PROVINCES.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block body-regular font-medium mb-2">Postal Code *</label>
              <input
                type="text"
                value={businessInfo.postalCode}
                onChange={(e) => setBusinessInfo({ ...businessInfo, postalCode: e.target.value.toUpperCase() })}
                className="input-clean"
                placeholder="M5V 1A1"
                maxLength={7}
                data-testid="input-postal"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="notice-info flex items-start gap-3">
        <Info className="w-5 h-5 text-[var(--brand-green)] flex-shrink-0 mt-0.5" />
        <p className="body-small">Service availability is subject to location review.</p>
      </div>
    </div>
  );
};

const StepServiceTerms = () => (
  <div>
    <div className="card-flat mb-6">
      <h3 className="heading-3 mb-5">What to Expect</h3>
      <div className="space-y-5">
        {[
          { num: 1, title: "Order Confirmation", desc: "Executive Mat will notify you within 1 business day with your first delivery timing." },
          { num: 2, title: "First Delivery & Invoice", desc: "Your first invoice is charged on the first delivery date." },
          { num: 3, title: "Ongoing Service", desc: "Mats are serviced every 8 weeks. Billing continues every 4 weeks thereafter." }
        ].map((step) => (
          <div key={step.num} className="flex items-start gap-4">
            <div className="step-number w-8 h-8 text-sm">{step.num}</div>
            <div>
              <p className="body-regular font-medium">{step.title}</p>
              <p className="body-small mt-1">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="card-flat text-center py-5">
        <p className="heading-3 text-base">{SERVICE_INFO.billingFrequency}</p>
        <p className="body-small mt-1">Billing Cycle</p>
      </div>
      <div className="card-flat text-center py-5">
        <p className="heading-3 text-base">{SERVICE_INFO.serviceFrequency}</p>
        <p className="body-small mt-1">Service Cycle</p>
      </div>
    </div>
  </div>
);

const StepAgreement = ({ agreementAccepted, setAgreementAccepted, hasScrolledAgreement, setHasScrolledAgreement, agreementPdfUrl }) => {
  const scrollContainerRef = useRef(null);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Check if scrolled to bottom (with 20px tolerance)
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledAgreement(true);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="heading-3 mb-3">Service Agreement</h3>
        <p className="body-small text-[var(--text-secondary)] mb-4">
          Please read the entire agreement below. You must scroll through all terms before accepting.
        </p>
        
        <div 
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="h-[400px] overflow-y-auto border border-[var(--border-light)] rounded-lg p-5 bg-[var(--bg-secondary)]"
          data-testid="agreement-scroll-container"
        >
          <pre className="whitespace-pre-wrap font-sans text-sm text-[var(--text-primary)] leading-relaxed">
            {AGREEMENT_TEXT}
          </pre>
        </div>
        
        {!hasScrolledAgreement && (
          <p className="body-small text-[var(--text-secondary)] mt-3 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Scroll to the bottom to continue
          </p>
        )}
      </div>

      <label className={`flex items-start gap-3 p-5 border rounded-lg transition-colors ${
        hasScrolledAgreement 
          ? 'border-[var(--border-light)] cursor-pointer hover:border-[var(--border-medium)]' 
          : 'border-[var(--border-light)] bg-[var(--bg-secondary)] cursor-not-allowed opacity-60'
      }`}>
        <Checkbox
          id="agreement"
          checked={agreementAccepted}
          onCheckedChange={hasScrolledAgreement ? setAgreementAccepted : undefined}
          disabled={!hasScrolledAgreement}
          className="mt-0.5"
          data-testid="agreement-checkbox"
        />
        <span className="body-regular">
          I have read and agree to the{' '}
          <a 
            href={agreementPdfUrl || COMPANY_INFO.serviceAgreementPdf} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[var(--brand-green)] font-medium hover:underline"
          >
            Executive Mat Service Agreement
          </a>.
        </span>
      </label>
    </div>
  );
};

const StepPayment = ({ paymentMethod, setPaymentMethod, cardInfo, setCardInfo }) => (
  <div>
    <div className="flex gap-3 mb-6">
      <button
        onClick={() => setPaymentMethod('credit')}
        className={`selectable flex-1 p-4 text-center rounded-lg border ${
          paymentMethod === 'credit' ? 'selected' : 'border-[var(--border-light)]'
        }`}
        data-testid="payment-credit"
      >
        <span className="body-regular font-medium">Credit Card</span>
      </button>
      <button
        onClick={() => setPaymentMethod('ach')}
        className={`selectable flex-1 p-4 text-center rounded-lg border ${
          paymentMethod === 'ach' ? 'selected' : 'border-[var(--border-light)]'
        }`}
        data-testid="payment-ach"
      >
        <span className="body-regular font-medium">ACH / Bank Transfer</span>
      </button>
    </div>

    {paymentMethod === 'credit' && (
      <div className="space-y-5">
        <div>
          <label className="block body-regular font-medium mb-2">Name on Card *</label>
          <input
            type="text"
            value={cardInfo.nameOnCard}
            onChange={(e) => setCardInfo({ ...cardInfo, nameOnCard: e.target.value })}
            className="input-clean"
            placeholder="Full name as shown on card"
          />
        </div>
        <div>
          <label className="block body-regular font-medium mb-2">Card Number *</label>
          <input
            type="text"
            value={cardInfo.cardNumber}
            onChange={(e) => setCardInfo({ ...cardInfo, cardNumber: e.target.value })}
            className="input-clean"
            placeholder="1234 5678 9012 3456"
            maxLength={19}
          />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <div>
            <label className="block body-regular font-medium mb-2">Expiry Date *</label>
            <input
              type="text"
              value={cardInfo.expiry}
              onChange={(e) => setCardInfo({ ...cardInfo, expiry: e.target.value })}
              className="input-clean"
              placeholder="MM/YY"
              maxLength={5}
            />
          </div>
          <div>
            <label className="block body-regular font-medium mb-2">CVV *</label>
            <input
              type="text"
              value={cardInfo.cvv}
              onChange={(e) => setCardInfo({ ...cardInfo, cvv: e.target.value })}
              className="input-clean"
              placeholder="123"
              maxLength={4}
            />
          </div>
        </div>
      </div>
    )}

    {paymentMethod === 'ach' && (
      <div className="card-flat">
        <p className="body-regular text-[var(--text-secondary)]">
          ACH payment setup will be completed after order confirmation. Our team will contact you with bank transfer details.
        </p>
      </div>
    )}

    <div className="notice-info mt-6 flex items-start gap-3">
      <Info className="w-5 h-5 text-[var(--brand-green)] flex-shrink-0 mt-0.5" />
      <p className="body-small">Your card will be charged every 4 weeks starting from your first delivery date.</p>
    </div>
  </div>
);

const StepReview = ({ cart, products, pricing, selectedTerm, businessInfo, getFullAddress, isQuote, showPaymentFollowUp, followUpAcknowledged, setFollowUpAcknowledged }) => {
  const term = AGREEMENT_TERMS.find(t => t.months === selectedTerm);
  
  return (
    <div className="space-y-5">
      {/* Follow-up payment notice */}
      {showPaymentFollowUp && (
        <div className="p-5 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-4">
            <Phone className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Payment Collection</h3>
              <p className="text-blue-800 text-sm mb-4">
                We'll contact you within 1 business day to confirm your order and securely collect payment details before service starts.
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={followUpAcknowledged}
                  onCheckedChange={setFollowUpAcknowledged}
                  className="mt-0.5"
                  data-testid="followup-checkbox"
                />
                <span className="text-sm text-blue-800">
                  I understand payment will be collected after order confirmation.
                </span>
              </label>
            </div>
          </div>
        </div>
      )}
      
      {isQuote && (
        <div className="p-5 bg-[var(--bg-secondary)] border border-[var(--border-light)] rounded-lg">
          <div className="flex items-start gap-4">
            <FileText className="w-6 h-6 text-[var(--brand-green)] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-[var(--text-primary)] mb-2">Quote Request</h3>
              <p className="text-[var(--text-secondary)] text-sm">
                We'll send a detailed quote to your email. You can review and approve it when you're ready — no commitment required.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="card-flat">
        <h3 className="heading-3 text-base mb-4">Order Details</h3>
        <div className="space-y-2">
          {cart.filter(item => item.quantity > 0).map(item => {
            const product = products.find(p => p.id === item.id || p.sku === item.sku);
            if (!product) return null;
            return (
              <div key={item.id} className="flex justify-between">
                <span className="body-regular">{product.name} × {item.quantity}</span>
                <span className="body-regular font-medium">${(product.price_e4w * item.quantity).toFixed(2)}/4wks</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card-flat">
        <h3 className="heading-3 text-base mb-4">Business Information</h3>
        <div className="space-y-2 body-regular">
          <p><span className="text-[var(--text-secondary)]">Business:</span> {businessInfo.businessName}</p>
          <p><span className="text-[var(--text-secondary)]">Contact:</span> {businessInfo.contactName}</p>
          <p><span className="text-[var(--text-secondary)]">Email:</span> {businessInfo.email}</p>
          <p><span className="text-[var(--text-secondary)]">Phone:</span> {businessInfo.phone}</p>
          <p><span className="text-[var(--text-secondary)]">Address:</span> {getFullAddress()}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="card-flat">
          <h3 className="heading-3 text-base mb-2">Agreement Term</h3>
          <p className="body-regular">{term?.label}</p>
        </div>
        <div className="card-flat">
          <h3 className="heading-3 text-base mb-2">Service</h3>
          <p className="body-regular">{SERVICE_INFO.serviceFrequency}</p>
        </div>
      </div>
    </div>
  );
};

// Reward calculation helper
const getRewardInfo = (weeklyTotal, rewardTiers) => {
  if (!rewardTiers || rewardTiers.length === 0) {
    return { currentReward: 0, nextReward: 0, deltaToNext: 0, isTopTier: true, progress: 0, currentTierIndex: 0 };
  }
  
  const sortedTiers = [...rewardTiers].sort((a, b) => a.min_weekly - b.min_weekly);
  
  // Find current tier
  let currentTierIndex = sortedTiers.findIndex(
    tier => weeklyTotal >= tier.min_weekly && weeklyTotal <= tier.max_weekly
  );
  
  if (currentTierIndex === -1) {
    currentTierIndex = sortedTiers.length - 1;
  }
  
  const currentTier = sortedTiers[currentTierIndex];
  const isTopTier = currentTierIndex === sortedTiers.length - 1 || weeklyTotal > 100;
  
  // Get next tier
  const nextTierIndex = Math.min(currentTierIndex + 1, sortedTiers.length - 1);
  const nextTier = sortedTiers[nextTierIndex];
  
  // Calculate delta
  let deltaToNext = 0;
  let progress = 0;
  
  if (!isTopTier) {
    deltaToNext = Math.max(0, nextTier.min_weekly - weeklyTotal);
    const tierRange = currentTier.max_weekly - currentTier.min_weekly;
    progress = tierRange > 0 ? (weeklyTotal - currentTier.min_weekly) / tierRange : 0;
    progress = Math.min(1, Math.max(0, progress));
  } else {
    progress = 1;
  }
  
  return {
    currentReward: currentTier?.reward_amount || 0,
    nextReward: isTopTier ? (currentTier?.reward_amount || 0) : (nextTier?.reward_amount || 0),
    deltaToNext: Math.ceil(deltaToNext * 100) / 100,
    isTopTier,
    progress,
    currentTierIndex
  };
};

// Order Summary Sidebar
const OrderSummary = ({ cart, products, pricing, selectedTerm, updateQuantity, rewardTiers, isQuote }) => {
  const term = AGREEMENT_TERMS.find(t => t.months === selectedTerm);
  const totalMats = getTotalMatCount(cart);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevRewardRef = useRef(0);

  const weeklyTotal = pricing.weeklyEquivalent;
  const rewardInfo = getRewardInfo(weeklyTotal, rewardTiers);

  // Animate when reward changes
  useEffect(() => {
    if (prevRewardRef.current !== rewardInfo.currentReward && totalMats > 0) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 800);
      prevRewardRef.current = rewardInfo.currentReward;
      return () => clearTimeout(timer);
    }
  }, [rewardInfo.currentReward, totalMats]);

  // Calculate weekly cost for each product
  const getProductWeeklyCost = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return 0;
    const termDiscount = term?.discount || 0;
    const baseWeekly = product.price_e4w / 4;
    const discountedWeekly = baseWeekly * (1 - termDiscount);
    const withCharges = discountedWeekly * (1 + SERVICE_CHARGES.defCharge + SERVICE_CHARGES.greenCharge);
    return withCharges;
  };

  return (
    <div className="sticky top-24 space-y-4">
      {/* Rewards Card - Compact Version */}
      {totalMats > 0 && !isQuote && (
        <div className={`relative overflow-hidden rounded-xl border-2 border-[#f59e0b] shadow-lg shadow-amber-100 bg-gradient-to-br from-[#fffbeb] via-[#fef3c7] to-[#fde68a] p-4 transition-all duration-300 ${isAnimating ? 'scale-[1.02] shadow-xl shadow-amber-200' : ''}`}>
          <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -translate-x-full transition-transform duration-1000 ${isAnimating ? 'translate-x-full' : ''}`} />
          
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#f59e0b] to-[#d97706] flex items-center justify-center shadow-md">
                <Gift className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-bold text-[#78350f]">Rewards Program</h3>
            </div>

            <div className={`mb-1 transition-all duration-300 ${isAnimating ? 'scale-105' : ''}`}>
              <p className="text-lg font-bold text-[#78350f] leading-tight">
                Earn a ${rewardInfo.currentReward} Amazon.ca Gift Card
              </p>
            </div>

            <p className="text-sm text-[#92400e] mb-3">
              Weekly total: <span className="font-semibold">${weeklyTotal.toFixed(2)}</span>
            </p>

            {!rewardInfo.isTopTier ? (
              <div className="mb-2 p-2 bg-white/60 rounded-lg border border-[#fbbf24]">
                <p className="text-sm font-bold text-[#92400e]">
                  Add ${rewardInfo.deltaToNext.toFixed(2)}/week to unlock ${rewardInfo.nextReward}
                </p>
              </div>
            ) : (
              <div className="mb-2 p-2 bg-[#fbbf24]/30 rounded-lg border border-[#f59e0b] flex items-center gap-2">
                <Check className="w-4 h-4 text-[#78350f]" />
                <p className="text-sm font-bold text-[#78350f]">Top reward level reached!</p>
              </div>
            )}

            {!rewardInfo.isTopTier && (
              <div className="mb-2">
                <div className="flex justify-between text-xs font-medium text-[#92400e] mb-1">
                  <span>${rewardInfo.currentReward}</span>
                  <span>${rewardInfo.nextReward}</span>
                </div>
                <div className="h-2 bg-[#fef3c7] rounded-full overflow-hidden border border-[#fde68a]">
                  <div 
                    className="h-full bg-gradient-to-r from-[#f59e0b] to-[#d97706] rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${rewardInfo.progress * 100}%` }}
                  />
                </div>
              </div>
            )}

            <p className="text-xs text-[#a16207] leading-snug">
              Rewards issued after first successful payment.
            </p>
          </div>
        </div>
      )}

      {/* Order Summary Card */}
      <div className="card-clean">
        <h3 className="heading-2 text-lg mb-6">{isQuote ? 'Quote Summary' : 'Order Summary'}</h3>
        
        {totalMats > 0 ? (
          <>
            <div className="space-y-2 pb-4 border-b border-[var(--border-light)]">
              {cart.filter(item => item.quantity > 0).map(item => {
                const product = products.find(p => p.id === item.id || p.sku === item.sku);
                if (!product) return null;
                return (
                  <div key={item.id} className="flex justify-between body-small">
                    <span className="text-[var(--text-secondary)]">{product.dimensions} × {item.quantity}</span>
                    <span>${(product.price_e4w * item.quantity).toFixed(2)}</span>
                  </div>
                );
              })}
            </div>

            <div className="space-y-2 py-4 border-b border-[var(--border-light)] body-small">
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Subtotal</span>
                <span>${pricing.subtotal.toFixed(2)}</span>
              </div>
              {pricing.discount > 0 && (
                <div className="flex justify-between text-[var(--brand-green)]">
                  <span>Term Discount ({Math.round(pricing.discount)}%)</span>
                  <span>-${pricing.savings.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">DEF Charge (9%)</span>
                <span>${pricing.defCharge.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Green Charge (12%)</span>
                <span>${pricing.greenCharge.toFixed(2)}</span>
              </div>
            </div>

            <div className="py-4">
              <div className="flex justify-between items-baseline mb-1">
                <span className="heading-3 text-base">Weekly</span>
                <span className="text-3xl font-bold text-[var(--text-primary)]">${pricing.weeklyEquivalent.toFixed(2)}</span>
              </div>
              <div className="flex justify-between body-small">
                <span className="text-[var(--text-secondary)]">Every 4 weeks</span>
                <span className="text-[var(--text-secondary)]">${pricing.total.toFixed(2)}</span>
              </div>
            </div>

            {pricing.minimumApplies && (
              <div className="notice-warning text-sm mt-4">
                <p className="text-[#92400e]">
                  A minimum monthly invoice of ${SERVICE_CHARGES.minimumInvoice} applies.
                </p>
              </div>
            )}

            <div className="mt-4 card-flat text-center py-4">
              <p className="body-small">
                <span className="font-medium text-[var(--text-primary)]">{term?.label} term</span>
                <br />
                Billed {SERVICE_INFO.billingFrequency.toLowerCase()}
              </p>
            </div>
          </>
        ) : (
          <p className="body-regular text-[var(--text-secondary)] text-center py-8">
            Select mats to see your {isQuote ? 'quote' : 'order'} summary.
          </p>
        )}
      </div>
    </div>
  );
};

// Quote Confirmation
const QuoteConfirmation = ({ pricing, businessInfo, selectedTerm, cart, products, getFullAddress, quoteToken }) => {
  const term = AGREEMENT_TERMS.find(t => t.months === selectedTerm);

  return (
    <div className="min-h-screen bg-white">
      <div className="container section-padding">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 bg-[var(--brand-green)] text-white rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8" />
          </div>
          <h1 className="hero-medium mb-4">Quote Request Received!</h1>
          <p className="body-large mb-10">
            We'll send a detailed quote to {businessInfo.email} shortly. You can review and approve it when you're ready.
          </p>

          <div className="card-clean text-left mb-8">
            <h2 className="heading-2 text-lg mb-6">Quote Summary</h2>
            
            <div className="space-y-5">
              <div className="pb-4 border-b border-[var(--border-light)]">
                <p className="body-small text-[var(--text-secondary)] mb-1">Business</p>
                <p className="body-regular font-medium">{businessInfo.businessName}</p>
              </div>
              
              <div className="pb-4 border-b border-[var(--border-light)]">
                <p className="body-small text-[var(--text-secondary)] mb-1">Service Address</p>
                <p className="body-regular">{getFullAddress()}</p>
              </div>

              <div className="pb-4 border-b border-[var(--border-light)]">
                <p className="body-small text-[var(--text-secondary)] mb-2">Mats Included</p>
                {cart.filter(item => item.quantity > 0).map(item => {
                  const product = products.find(p => p.id === item.id || p.sku === item.sku);
                  if (!product) return null;
                  return <p key={item.id} className="body-regular">{product.name} × {item.quantity}</p>;
                })}
              </div>

              <div className="pb-4 border-b border-[var(--border-light)]">
                <p className="body-small text-[var(--text-secondary)] mb-1">Agreement Term</p>
                <p className="body-regular">{term?.label}</p>
              </div>

              <div>
                <p className="body-small text-[var(--text-secondary)] mb-1">Estimated Weekly Rate</p>
                <p className="text-3xl font-bold text-[var(--text-primary)]">${pricing.weeklyEquivalent.toFixed(2)}</p>
                <p className="body-small text-[var(--text-secondary)] mt-1">
                  Every 4 weeks: ${pricing.total.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="card-flat text-left mb-8">
            <h3 className="heading-3 mb-4">What Happens Next?</h3>
            <ul className="space-y-3">
              {[
                `Quote details sent to ${businessInfo.email}`,
                "Review the quote at your convenience",
                "Click the approval link when you're ready to start service"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-[var(--brand-green)] mt-1 flex-shrink-0" />
                  <span className="body-regular text-[var(--text-secondary)]">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="body-small text-[var(--text-secondary)]">
            Questions? Contact us at{' '}
            <a href="tel:1-877-720-6287" className="text-[var(--brand-green)] font-medium hover:underline">
              1-877-720-6287
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

// Order Confirmation
const OrderConfirmation = ({ pricing, businessInfo, selectedTerm, cart, products, getFullAddress, showPaymentFollowUp, rewardTiers }) => {
  const term = AGREEMENT_TERMS.find(t => t.months === selectedTerm);
  const rewardInfo = getRewardInfo(pricing.weeklyEquivalent, rewardTiers);

  return (
    <div className="min-h-screen bg-white">
      <div className="container section-padding">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 bg-[var(--brand-green)] text-white rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8" />
          </div>
          <h1 className="hero-medium mb-4">Welcome to Executive Mat!</h1>
          <p className="body-large mb-10">
            {showPaymentFollowUp 
              ? "Your order has been received. We'll contact you within 1 business day to confirm details and collect payment."
              : "Your service request has been received. We'll notify you within 1 business day with your first delivery timing."
            }
          </p>

          {/* Reward earned */}
          {rewardInfo.currentReward > 0 && (
            <div className="mb-8 p-6 bg-gradient-to-br from-[#fffbeb] to-[#fef3c7] border-2 border-[#f59e0b] rounded-xl">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Gift className="w-8 h-8 text-[#f59e0b]" />
                <span className="text-2xl font-bold text-[#78350f]">${rewardInfo.currentReward} Amazon.ca Gift Card</span>
              </div>
              <p className="text-[#92400e]">Your reward will be issued after your first successful payment!</p>
            </div>
          )}

          <div className="card-clean text-left mb-8">
            <h2 className="heading-2 text-lg mb-6">Order Confirmation</h2>
            
            <div className="space-y-5">
              <div className="pb-4 border-b border-[var(--border-light)]">
                <p className="body-small text-[var(--text-secondary)] mb-1">Business</p>
                <p className="body-regular font-medium">{businessInfo.businessName}</p>
              </div>
              
              <div className="pb-4 border-b border-[var(--border-light)]">
                <p className="body-small text-[var(--text-secondary)] mb-1">Service Address</p>
                <p className="body-regular">{getFullAddress()}</p>
              </div>

              <div className="pb-4 border-b border-[var(--border-light)]">
                <p className="body-small text-[var(--text-secondary)] mb-2">Mats Ordered</p>
                {cart.filter(item => item.quantity > 0).map(item => {
                  const product = products.find(p => p.id === item.id || p.sku === item.sku);
                  if (!product) return null;
                  return <p key={item.id} className="body-regular">{product.name} × {item.quantity}</p>;
                })}
              </div>

              <div className="pb-4 border-b border-[var(--border-light)]">
                <p className="body-small text-[var(--text-secondary)] mb-1">Agreement Term</p>
                <p className="body-regular">{term?.label}</p>
              </div>

              <div>
                <p className="body-small text-[var(--text-secondary)] mb-1">Weekly Rate</p>
                <p className="text-3xl font-bold text-[var(--text-primary)]">${pricing.weeklyEquivalent.toFixed(2)}</p>
                <p className="body-small text-[var(--text-secondary)] mt-1">
                  Every 4 weeks: ${pricing.total.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="card-flat text-left mb-8">
            <h3 className="heading-3 mb-4">What Happens Next?</h3>
            <ul className="space-y-3">
              {showPaymentFollowUp ? (
                <>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[var(--brand-green)] mt-1 flex-shrink-0" />
                    <span className="body-regular text-[var(--text-secondary)]">We'll call to confirm your order within 1 business day</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[var(--brand-green)] mt-1 flex-shrink-0" />
                    <span className="body-regular text-[var(--text-secondary)]">Payment details collected securely over the phone</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[var(--brand-green)] mt-1 flex-shrink-0" />
                    <span className="body-regular text-[var(--text-secondary)]">First delivery scheduled after confirmation</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[var(--brand-green)] mt-1 flex-shrink-0" />
                    <span className="body-regular text-[var(--text-secondary)]">Confirmation email sent to {businessInfo.email}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[var(--brand-green)] mt-1 flex-shrink-0" />
                    <span className="body-regular text-[var(--text-secondary)]">We'll contact you within 1 business day with delivery details</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[var(--brand-green)] mt-1 flex-shrink-0" />
                    <span className="body-regular text-[var(--text-secondary)]">First invoice charged on delivery date</span>
                  </li>
                </>
              )}
            </ul>
          </div>

          <p className="body-small text-[var(--text-secondary)]">
            Questions? Contact us at{' '}
            <a href="tel:1-877-720-6287" className="text-[var(--brand-green)] font-medium hover:underline">
              1-877-720-6287
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
